import { FC, useEffect, useRef, useState } from 'react';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';

const DEFAULT_ASPECT = 16 / 9;

let modPromise: Promise<typeof import('pptx-preview')> | null = null;
export const preloadPptxModule = () => (modPromise ??= import('pptx-preview'));

const deckKey = (url: string) => url.split('?')[0];
const deckCache = new Map<string, Promise<ArrayBuffer>>();
const aspectCache = new Map<string, number>();

const MC_NS = 'http://schemas.openxmlformats.org/markup-compatibility/2006';

export const resolveAlternateContent = async (buffer: ArrayBuffer): Promise<ArrayBuffer> => {
  try {
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(buffer);
    let changed = false;
    for (const slide of zip.file(/^ppt\/slides\/slide\d+\.xml$/)) {
      const xml = await slide.async('string');
      if (!xml.includes('<mc:AlternateContent')) continue;
      const doc = new DOMParser().parseFromString(xml, 'text/xml');
      if (doc.getElementsByTagName('parsererror').length) continue;
      let slideChanged = false;
      for (const block of Array.from(doc.getElementsByTagNameNS(MC_NS, 'AlternateContent'))) {
        if (!block.isConnected) continue;
        const fallback = Array.from(block.children).find(
          child => child.namespaceURI === MC_NS && child.localName === 'Fallback',
        );
        if (!fallback) continue;
        block.replaceWith(...Array.from(fallback.childNodes));
        slideChanged = true;
      }
      if (!slideChanged) continue;
      zip.file(slide.name, new XMLSerializer().serializeToString(doc));
      changed = true;
    }
    return changed ? await zip.generateAsync({ type: 'arraybuffer' }) : buffer;
  } catch {
    return buffer;
  }
};

export const prefetchDeck = (url: string) => {
  const key = deckKey(url);
  const cached = deckCache.get(key);
  if (cached) return cached;
  const promise = fetch(url)
    .then(response => {
      if (!response.ok) throw new Error(`fetch failed: HTTP ${response.status}`);
      return response.arrayBuffer();
    })
    .then(resolveAlternateContent);
  deckCache.set(key, promise);
  promise.catch(() => deckCache.delete(key));
  if (deckCache.size > 2) {
    const oldest = deckCache.keys().next().value;
    if (oldest !== undefined && oldest !== key) deckCache.delete(oldest);
  }
  return promise;
};

type Previewer = {
  preview: (file: ArrayBuffer) => Promise<unknown>;
  renderSingleSlide: (index: number) => void;
  slideCount: number;
};

type InitFn = (dom: HTMLElement, options: { width: number; height: number; mode: 'slide' }) => Previewer;

export const PptxPreviewViewer: FC<{
  url: string;
  current: number;
  onReady: (slideCount?: number) => void;
  onError: (message: string) => void;
}> = ({ url, current, onReady, onError }) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const previewerRef = useRef<Previewer | null>(null);
  const bufferRef = useRef<ArrayBuffer | null>(null);
  const aspectRef = useRef(DEFAULT_ASPECT);
  const currentRef = useRef(current);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    currentRef.current = current;
    previewerRef.current?.renderSingleSlide(current);
  }, [current]);

  useEffect(() => {
    const outer = outerRef.current;
    const stage = stageRef.current;
    if (!outer || !stage) return;
    let cancelled = false;
    let initFn: InitFn | null = null;
    let chain: Promise<unknown> = Promise.resolve();
    let timer: number | undefined;
    let lastWidth = 0;
    let lastHeight = 0;
    setReady(false);
    aspectRef.current = aspectCache.get(deckKey(url)) ?? DEFAULT_ASPECT;

    const renderDeck = async (): Promise<Previewer | null> => {
      const buffer = bufferRef.current;
      if (cancelled || !initFn || !buffer) return null;
      const aspect = aspectRef.current;
      const width = Math.floor(Math.min(outer.clientWidth, outer.clientHeight * aspect));
      const height = Math.floor(width / aspect);
      if (previewerRef.current && width === lastWidth && height === lastHeight) return previewerRef.current;
      lastWidth = width;
      lastHeight = height;
      previewerRef.current = null;
      stage.replaceChildren();
      setBox({ width, height });
      const previewer = initFn(stage, { width, height, mode: 'slide' });
      await previewer.preview(buffer);
      if (cancelled) return null;
      if (currentRef.current !== 0) previewer.renderSingleSlide(currentRef.current);
      previewerRef.current = previewer;
      return previewer;
    };

    const enqueueRender = () => {
      const next = chain.then(renderDeck);
      chain = next.catch(() => null);
      return next;
    };

    (async () => {
      const [mod, buffer] = await Promise.all([preloadPptxModule(), prefetchDeck(url)]);
      if (cancelled) return;
      bufferRef.current = buffer;
      initFn = mod.init as InitFn;
      let previewer = await enqueueRender();
      if (cancelled || !previewer) return;
      const pptx = (previewer as { pptx?: { width?: number; height?: number } }).pptx;
      if (pptx?.width && pptx.height) {
        const aspect = pptx.width / pptx.height;
        aspectCache.set(deckKey(url), aspect);
        if (Math.abs(aspect - aspectRef.current) > aspectRef.current * 0.01) {
          aspectRef.current = aspect;
          previewer = (await enqueueRender()) ?? previewer;
          if (cancelled) return;
        }
      }
      setReady(true);
      onReady(previewer.slideCount || undefined);
    })().catch((e: Error) => !cancelled && onError(e.message));

    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        enqueueRender().catch((e: Error) => !cancelled && onError(e.message));
      }, 200);
    });
    observer.observe(outer);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      observer.disconnect();
      previewerRef.current = null;
      bufferRef.current = null;
      stage.replaceChildren();
    };
  }, [url]);

  return (
    <div ref={outerRef} className="tw-relative tw-size-full tw-flex tw-items-center tw-justify-center">
      {!ready && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-6 [&>div]:!tw-h-auto">
          <ThreeDotLoader />
          <span className="tw-font-atkinson tw-text-sm tw-text-stone-300">Preparing your slides…</span>
        </div>
      )}
      <div
        ref={stageRef}
        style={box ?? undefined}
        className={`tw-rounded-3xl tw-overflow-hidden [&_.pptx-preview-wrapper-next]:!tw-hidden [&_.pptx-preview-wrapper-pagination]:!tw-hidden [&_.pptx-preview-wrapper]:!tw-bg-white [&_.pptx-preview-wrapper]:!tw-overflow-hidden [&_p]:!tw-whitespace-pre-wrap [&_p]:!tw-min-h-[1lh] ${
          ready ? '' : 'tw-invisible'
        }`}
      />
    </div>
  );
};
